/** 传给后端的容量上限：100TB（按 1TB=1024GB） */
export const HARDWARE_CAPACITY_MAX_GB = 100 * 1024

const TB_IN_GB = 1024

function unitToGb (n: number, unit?: string): number {
  if (!Number.isFinite(n)) return 0
  const u = (unit || 'GB').toUpperCase()
  if (u === 'TB' || u === 'T') return n * TB_IN_GB
  return n
}

/** 与列表 memory_capacity（MB）一致：1GB = 1024 MB */
export const HARDWARE_MB_PER_GB = 1024

/** 小于 0 按 0，大于 100TB 按 100TB（GB） */
export function clampHardwareCapacityGb (n: number): number {
  if (!Number.isFinite(n)) return 0
  if (n < 0) return 0
  if (n > HARDWARE_CAPACITY_MAX_GB) return HARDWARE_CAPACITY_MAX_GB
  return n
}

/** 接口侧单位为 MB：在 GB 域内裁剪后转为 MB（四舍五入取整） */
export function hardwareCapacityGbToMb (gb: number): number {
  return Math.round(clampHardwareCapacityGb(gb) * HARDWARE_MB_PER_GB)
}

/** 如 `<512GB`、`<512`（图例最小档：0～该阈值） */
export function isLessThanCapacityLabel (label: string): boolean {
  const s = String(label).trim()
  if (/^<\s*\d/.test(s)) return true
  if (/\d/.test(s) && /以下/.test(s)) return true
  return false
}

function parseLessThanThresholdGb (label: string): number | null {
  const s = String(label).trim().replace(/\s+/g, '')
  const m1 = s.match(/^<(\d+(?:\.\d+)?)(GB|G|TB|T)?$/i)
  if (m1) return unitToGb(parseFloat(m1[1]), m1[2])
  const m2 = s.match(/^<(\d+(?:\.\d+)?)(GB|G|TB|T)/i)
  if (m2) return unitToGb(parseFloat(m2[1]), m2[2])
  const m3 = s.match(/(\d+(?:\.\d+)?)(GB|G|TB|T)?以下/i)
  if (m3) return unitToGb(parseFloat(m3[1]), m3[2])
  return null
}

/** 非「小于」档：解析该档位的下限（GB），如 512GB→512、1TB→1024 */
export function parseTierFloorGb (label: string): number | null {
  if (isLessThanCapacityLabel(label)) return null
  const s = String(label).trim().replace(/\s+/g, '')
  const single = s.match(/^(\d+(?:\.\d+)?)(GB|G|TB|T)?$/i)
  if (single) return unitToGb(parseFloat(single[1]), single[2])
  const m = s.match(/(\d+(?:\.\d+)?)(GB|G|TB|T)/i)
  if (m) return unitToGb(parseFloat(m[1]), m[2])
  const num = s.match(/^(\d+(?:\.\d+)?)$/)
  if (num) return parseFloat(num[1])
  return null
}

/**
 * 根据当前下拉/饼图全部选项与选中项得到 [minGb, maxGb]：
 * - `<X` / 「以下」：0～X
 * - 其它档位（无「小于」语义）：min、max 均取该档解析下限（单点区间，与列表/接口筛选约定一致）
 *
 * allOptionNames 建议为饼图下发的全部档位 name（顺序任意，内部会按下限排序，用于校验选中项是否落在已知档位）。
 */
export function resolveCapacityRangeFromAdjacentOptions (
  selectedLabel: string,
  allOptionNames: string[]
): { minGb: number; maxGb: number } | null {
  if (selectedLabel == null || selectedLabel === '' || selectedLabel === 'Unknown') {
    return null
  }
  const names = [...new Set(allOptionNames.filter(Boolean) as string[])]
  if (names.length === 0) return null

  if (isLessThanCapacityLabel(selectedLabel)) {
    const hi = parseLessThanThresholdGb(selectedLabel)
    if (hi == null) return null
    return {
      minGb: clampHardwareCapacityGb(0),
      maxGb: clampHardwareCapacityGb(hi)
    }
  }

  const floorStarts: number[] = []
  for (const n of names) {
    if (isLessThanCapacityLabel(n)) continue
    const g = parseTierFloorGb(n)
    if (g != null && Number.isFinite(g)) floorStarts.push(g)
  }
  const sorted = [...new Set(floorStarts)].sort((a, b) => a - b)
  const selStart = parseTierFloorGb(selectedLabel)
  if (selStart == null || !Number.isFinite(selStart)) return null

  const idx = sorted.indexOf(selStart)
  if (idx === -1) return null

  const minGb = clampHardwareCapacityGb(selStart)
  // 非「<X」档位：不按相邻档拉宽区间，min/max 均取下限（与接口约定一致）
  return { minGb, maxGb: minGb }
}

/**
 * 仅根据文案解析（无选项列表时的回退），仍受 100TB 上限裁剪。
 */
export function parseCapacityLabelToGbRange (label: string): { minGb: number; maxGb: number } | null {
  if (label == null || label === '' || label === 'Unknown') return null
  const s = String(label).trim().replace(/\s+/g, '')

  const rangeMatch = s.match(
    /(\d+(?:\.\d+)?)\s*(GB|G|TB|T)?\s*[-~～]\s*(\d+(?:\.\d+)?)\s*(GB|G|TB|T)?/i
  )
  if (rangeMatch) {
    const a = unitToGb(parseFloat(rangeMatch[1]), rangeMatch[2])
    const b = unitToGb(parseFloat(rangeMatch[3]), rangeMatch[4])
    return {
      minGb: clampHardwareCapacityGb(Math.min(a, b)),
      maxGb: clampHardwareCapacityGb(Math.max(a, b))
    }
  }

  const geEq = s.match(/^>=(\d+(?:\.\d+)?)(GB|G|TB|T)?$/i)
  if (geEq) {
    const lo = unitToGb(parseFloat(geEq[1]), geEq[2])
    return { minGb: clampHardwareCapacityGb(lo), maxGb: HARDWARE_CAPACITY_MAX_GB }
  }

  if (s.includes('以上') || /^>\s*\d/.test(s)) {
    const m = s.match(/(\d+(?:\.\d+)?)\s*(GB|G|TB|T)?/i)
    if (m) {
      const lo = unitToGb(parseFloat(m[1]), m[2])
      return { minGb: clampHardwareCapacityGb(lo), maxGb: HARDWARE_CAPACITY_MAX_GB }
    }
  }

  if (s.includes('以下') || /^<\s*\d/.test(s)) {
    const m = s.match(/(\d+(?:\.\d+)?)\s*(GB|G|TB|T)?/i)
    if (m) {
      const hi = unitToGb(parseFloat(m[1]), m[2])
      return { minGb: 0, maxGb: clampHardwareCapacityGb(hi) }
    }
  }

  const single = s.match(/^(\d+(?:\.\d+)?)\s*(GB|G|TB|T)?$/i)
  if (single) {
    const v = unitToGb(parseFloat(single[1]), single[2])
    return { minGb: clampHardwareCapacityGb(v), maxGb: clampHardwareCapacityGb(v) }
  }

  const num = s.match(/^(\d+(?:\.\d+)?)$/)
  if (num) {
    const v = parseFloat(num[1])
    return { minGb: clampHardwareCapacityGb(v), maxGb: clampHardwareCapacityGb(v) }
  }

  return null
}

/** 先按相邻档位解析；失败则回退文案解析 */
export function resolveCapacityRangeWithFallback (
  selectedLabel: string | undefined,
  allOptionNames: string[]
): { minGb: number; maxGb: number } | null {
  if (!selectedLabel) return null
  const adjacent = resolveCapacityRangeFromAdjacentOptions(selectedLabel, allOptionNames)
  if (adjacent != null) return adjacent
  return parseCapacityLabelToGbRange(selectedLabel)
}
